import { useState, type JSX } from 'react'
import type { BalanceRow } from '../api'
import { monthLabel, yen } from '../format'
import { CHART_INK, POLARITY } from '../charts/palette'
import { compactYen, domainFor, monthLabelY, plotValue, ticksFor, yFor } from '../charts/scale'
import { PlanBadge } from './PlanToggle'
import { SourceNote } from './FreshnessStatus'

// Drift: audited bank balance minus the balance this database computes.
//
// The first version of this chart drew both balances as two lines and shaded
// the gap. It was useless. Drift runs around ¥1M against balances of ¥65M, so
// at any scale that fits the balances the two lines sit on top of each other
// and the thing the chart exists to show is a hairline. Plotting the DIFFERENCE
// gives it the whole axis.
//
// The balances themselves are not lost — they are in the tooltip, and in the
// reconciliation table below.
//
// Drift is expected and permanent (invariant #9: reconciliation is a standing
// view, not a bug to be closed). What matters is whether it is stable or
// growing, which is a shape question, so bars against a zero line is the right
// form. Above the line means the bank holds more than we computed; below means
// less. Blue and coral, never green/red — the sign is already carried by
// position, and green/red is the worst pair for deuteranopia.

const W = 720
// H and PAD.bottom grew together (from 220/34) so the plot box is unchanged
// and only the label gutter got taller for the angled month labels.
const H = 234
const PAD = { top: 18, right: 16, bottom: 48, left: 62 }
const PLOT_W = W - PAD.left - PAD.right
const PLOT_H = H - PAD.top - PAD.bottom
const LABEL_Y = monthLabelY(PAD.top + PLOT_H)

export function ReconciliationChart({
  rows,
  sourceNote,
}: {
  rows: BalanceRow[]
  /** Set when a source behind this chart is out of date; see freshness.ts. */
  sourceNote?: string | null
}): JSX.Element {
  const [hover, setHover] = useState<number | null>(null)

  const points = rows
    .map((row) => ({ row, drift: plotValue(row.drift) }))
    .filter((point): point is { row: BalanceRow; drift: number } => point.drift !== null)

  if (points.length === 0) {
    return (
      <section className="panel">
        <h2>Reconciliation drift</h2>
        <SourceNote text={sourceNote ?? null} />
        <p className="notice">No audited balances on record yet, so there is nothing to compare.</p>
      </section>
    )
  }

  const { min, max } = domainFor(points.map((point) => point.drift))
  const band = PLOT_W / points.length
  const barW = Math.min(30, band * 0.46)
  const y = (value: number): number => yFor(value, min, max, PAD.top, PLOT_H)
  const zero = y(0)
  const centre = (index: number): number => PAD.left + band * index + band / 2

  const worst = points.reduce((a, b) => (Math.abs(b.drift) > Math.abs(a.drift) ? b : a))
  const active = hover === null ? null : (points[hover] ?? null)

  return (
    <section className="panel chart-panel">
      <header className="chart-head">
        <div>
          <h2>Reconciliation drift</h2>
          <SourceNote text={sourceNote ?? null} />
          <p className="chart-sub">
            Audited bank balance minus the balance computed here. Above the line the bank holds
            more than we account for; below, less. Largest so far {yen(worst.row.drift)} in{' '}
            {monthLabel(worst.row.month)}.
          </p>
        </div>
        <div className="chart-controls">
          {/* Audited against computed — both sides reported, no plan in either.
              Fixed badge rather than the plan switch the forecast charts carry. */}
          <PlanBadge>Actuals only · no plan input</PlanBadge>
          <ul className="legend">
            <li>
              <span className="swatch" style={{ background: POLARITY.positive }} /> Bank higher
            </li>
            <li>
              <span className="swatch" style={{ background: POLARITY.negative }} /> Bank lower
            </li>
          </ul>
        </div>
      </header>

      <div className="chart-wrap">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="chart"
          role="img"
          aria-label="Reconciliation drift by month. The audited and computed balances behind it are in the reconciliation table below."
          onMouseLeave={() => setHover(null)}
        >
          {ticksFor(min, max).map((tick) => (
            <g key={tick}>
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={y(tick)}
                y2={y(tick)}
                stroke={Math.abs(tick) < 1e-9 ? CHART_INK.axis : CHART_INK.grid}
              />
              <text x={PAD.left - 8} y={y(tick) + 3} textAnchor="end" className="tick">
                {compactYen(tick)}
              </text>
            </g>
          ))}

          {points.map((point, index) => {
            const top = point.drift >= 0 ? y(point.drift) : zero
            const height = Math.abs(y(point.drift) - zero)
            return (
              <rect
                key={point.row.month}
                x={centre(index) - barW / 2}
                y={top}
                width={barW}
                height={Math.max(1, height)}
                rx={3}
                fill={point.drift >= 0 ? POLARITY.positive : POLARITY.negative}
                opacity={hover === null || hover === index ? 1 : 0.55}
              />
            )
          })}

          {points.map((point, index) => (
            <text key={`x-${point.row.month}`} x={centre(index)} y={LABEL_Y} className="tick" textAnchor="end" transform={`rotate(-35 ${centre(index)} ${LABEL_Y})`}>
              {monthLabel(point.row.month)}
            </text>
          ))}

          {points.map((point, index) => (
            <rect
              key={`hit-${point.row.month}`}
              x={PAD.left + band * index}
              y={PAD.top}
              width={band}
              height={PLOT_H}
              fill="transparent"
              onMouseEnter={() => setHover(index)}
            />
          ))}
        </svg>

        {hover !== null && active !== null && (
          <div className="tooltip" style={{ left: `${(centre(hover) / W) * 100}%` }}>
            <strong>{monthLabel(active.row.month)}</strong>
            <span>
              Audited <b>{yen(active.row.auditedBalance)}</b>
            </span>
            <span>
              Computed <b>{yen(active.row.computedBalance)}</b>
            </span>
            <span>
              Drift <b>{yen(active.row.drift)}</b>
            </span>
            <span className="tooltip-note">source: {active.row.source}</span>
          </div>
        )}
      </div>
    </section>
  )
}
